import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const NONE = "__none__";
const clean = (v) => (v == null ? "" : String(v)).trim();
// Blank / null / whitespace-only database values map to the reserved NONE option.
export const optionKey = (v) => clean(v).toLowerCase() || NONE;
export const matchesOption = (raw, selected) => selected === "all" || optionKey(raw) === optionKey(selected === NONE ? "" : selected);

// Distinct options from database values: trimmed, case-insensitive de-duplicated, blanks shown as noneLabel.
export const dbOptions = (values, noneLabel) => {
  const seen = new Map();
  (values || []).forEach((v) => {
    const k = optionKey(v);
    if (!seen.has(k)) seen.set(k, k === NONE ? [NONE, noneLabel] : [clean(v), clean(v)]);
  });
  return [...seen.values()];
};

// Never hands Radix an empty value: blank/null options are dropped, duplicates (case-insensitive) removed.
export const safeOptions = (options) => {
  const seen = new Set();
  return (options || []).filter(([v]) => {
    const k = clean(v).toLowerCase();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

export function useRowFilter(rows, extra = () => true) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const categoryOptions = useMemo(() => dbOptions((rows || []).map((r) => r.category), "(No category)"), [rows]);
  const categories = useMemo(() => categoryOptions.map(([v]) => v).filter((v) => v !== NONE), [categoryOptions]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (rows || []).filter((r) => matchesOption(r.category, category) &&
      (!s || (r.name || "").toLowerCase().includes(s) || (r.sku || "").toLowerCase().includes(s)) && extra(r));
  }, [rows, q, category, extra]);
  return { q, setQ, category, setCategory, categories, categoryOptions, filtered };
}

export const FilterBar = ({ f, total, children }) => (
  <div className="mb-4 flex flex-wrap items-center gap-2">
    <div className="relative w-full sm:w-72">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input value={f.q} onChange={(e) => f.setQ(e.target.value)} placeholder="Search SKU or product…" className="pl-8 h-9" data-testid="filter-search-input" />
    </div>
    <SimpleSelect value={f.category} onChange={f.setCategory} testid="filter-category-select" className="h-9 w-full sm:w-56"
      options={[["all", "All categories"], ...f.categoryOptions]} />
    {children}
    <span className="ml-auto text-xs font-mono text-muted-foreground" data-testid="filter-count">{f.filtered.length} / {total} SKUs</span>
  </div>
);

export const SimpleSelect = ({ value, onChange, options, testid, className = "h-9 w-40" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={className} data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      {safeOptions(options).map(([v, l]) => <SelectItem key={v} value={String(v)}>{l || v}</SelectItem>)}
    </SelectContent>
  </Select>
);
